import { useSceneStore } from '@/stores/scene-store';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import { SceneDataElement } from '@/schemas/sceneDataSchema';
import { ValidationType, DataType } from '@/constants/validationTypes';

/**
 * Optimistic validity for entity-level indicators (validity icons, in-progress chips).
 *
 * - Pending non-empty value: optimistically valid (user has staged a change).
 * - No pending change, non-null value: optimistically valid (covers prefill data).
 * - Null boolean field: optimistically valid (checkbox auto-defaults to false on mount).
 * - Otherwise: trust the server's `valid` flag.
 */
export function isFieldOptimisticallyValid(
  field: SceneDataElement,
  pendingValue: SceneDataElement['value'] | undefined
): boolean {
  if (pendingValue !== undefined) {
    return pendingValue !== null && pendingValue !== '';
  }
  // If the field already has a non-null, non-empty value (e.g. from prefill),
  // treat it as optimistically valid even if the server hasn't confirmed yet.
  if (field.value !== null && field.value !== '') {
    return true;
  }
  // Boolean/checkbox fields auto-default to `false` when their component mounts.
  // Treat null-valued booleans as optimistically valid so entity indicators don't
  // show "in progress" just because the form hasn't been expanded yet.
  if (
    field.value === null &&
    field.validation_rules.some((r) => r.type === ValidationType.DataType && r.value === DataType.Boolean)
  ) {
    return true;
  }
  // Empty field with no pending change — only invalid if the server explicitly
  // reported it as invalid (valid === false).
  return field.valid !== false;
}

// Returns true when every field for a single entity is valid.
export function useEntityValidity(scope: string, entityIndex: number): boolean {
  return useSceneStore((state) => {
    const entityFields = Object.values(state.serverState).filter(
      (field) => field.scope === scope && field.entity_index === entityIndex
    );

    if (entityFields.length === 0) return false;

    return entityFields.every((field) => {
      const key = encodeFieldKey(field);
      const pendingValue = key in state.pendingChanges ? state.pendingChanges[key] : undefined;
      return isFieldOptimisticallyValid(field, pendingValue);
    });
  });
}

/**
 * Returns true when every field in the scene store matching any of the given
 * scopes is optimistically valid. Returns `null` when no fields are found for
 * any scope (the step hasn't been visited yet).
 */
export function useScopesValidity(scopes: string[]): boolean | null {
  return useSceneStore((state) => {
    if (scopes.length === 0) return null;

    const scopeSet = new Set(scopes);
    const fields = Object.values(state.serverState).filter((field) => scopeSet.has(field.scope));

    if (fields.length === 0) return null;

    return fields.every((field) => {
      const key = encodeFieldKey(field);
      const pendingValue = key in state.pendingChanges ? state.pendingChanges[key] : undefined;
      return isFieldOptimisticallyValid(field, pendingValue);
    });
  });
}
